// src/pages/courier/BranchFormModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X, GitBranch, Phone, Mail, User, Zap } from 'lucide-react';
import stateDistrictData from '../StatenDistrict.json';

/**
 * Generates branch code: {FirstLetterOfCourierName}-{STATE_ABBR2}-{DIST_ABBR3}
 * e.g. Courier="BlueDart", State="Odisha", District="Bhubaneswar" → B-OD-BHU
 */
const generateBranchCode = (courierName, stateName, districtName) => {
  if (!courierName || !stateName || !districtName) return '';
  const initial = courierName.trim().charAt(0).toUpperCase();
  // Take first 2 chars of state (removing spaces), uppercase
  const stateAbbr = stateName.replace(/\s+/g, '').substring(0, 2).toUpperCase();
  // Take first 3 chars of district (removing spaces), uppercase
  const distAbbr = districtName.replace(/\s+/g, '').substring(0, 3).toUpperCase();
  return `${initial}-${stateAbbr}-${distAbbr}`;
};

const BranchFormModal = ({
  isOpen,
  onClose,
  onSave,
  couriers = [],
  selectedCourierId = '',
  editingBranch = null
}) => {
  const statesList = useMemo(() => {
    return stateDistrictData?.states || [];
  }, []);

  const [formData, setFormData] = useState({
    courierId: selectedCourierId || '',
    branchName: '',
    branchCode: '',
    contactPerson: '',
    contactNumber: '',
    alternateContactNumber: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateName: '',
    stateCode: '',
    districtName: '',
    districtCode: '',
    pinCode: '',
    active: true
  });

  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingBranch) {
      const bStateName =
        editingBranch.address?.stateName ||
        editingBranch.address?.stateCode ||
        editingBranch.stateName ||
        editingBranch.state ||
        'Odisha';

      const bDistrictName =
        editingBranch.address?.districtName ||
        editingBranch.address?.districtCode ||
        editingBranch.districtName ||
        editingBranch.district ||
        '';

      // Populate districts for selected state
      const foundState = statesList.find(
        (s) => s.state.toLowerCase() === bStateName.toLowerCase()
      );
      const stateDistricts = foundState ? foundState.districts : [];

      const canonicalDistrict =
        stateDistricts.find((d) => d.toLowerCase() === bDistrictName.toLowerCase()) ||
        bDistrictName;

      setFormData({
        courierId: editingBranch.courierId || selectedCourierId || '',
        branchName: editingBranch.branchName || '',
        branchCode: editingBranch.branchCode || '',
        contactPerson: editingBranch.contactPerson || '',
        contactNumber: editingBranch.contactNumber || '',
        alternateContactNumber: editingBranch.alternateContactNumber || '',
        email: editingBranch.email || '',
        addressLine1: editingBranch.address?.addressLine1 || '',
        addressLine2: editingBranch.address?.addressLine2 || '',
        city: editingBranch.address?.city || '',
        stateName: foundState ? foundState.state : bStateName,
        stateCode: foundState ? foundState.state : bStateName,
        districtName: canonicalDistrict,
        districtCode: canonicalDistrict,
        pinCode: editingBranch.address?.pinCode || '',
        active: editingBranch.active !== undefined ? editingBranch.active : true
      });

      setAvailableDistricts(stateDistricts);
    } else {
      const defaultState =
        statesList.find((s) => s.state.toLowerCase() === 'odisha') || statesList[0];

      setFormData({
        courierId: selectedCourierId || (couriers[0]?.courierId || ''),
        branchName: '',
        branchCode: '',
        contactPerson: '',
        contactNumber: '',
        alternateContactNumber: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateName: defaultState?.state || '',
        stateCode: defaultState?.state || '',
        districtName: '',
        districtCode: '',
        pinCode: '',
        active: true
      });
      setAvailableDistricts(defaultState ? defaultState.districts : []);
    }
    setErrors({});
  }, [editingBranch, selectedCourierId, couriers, isOpen, statesList]);

  // Auto-generate branch code for NEW branches whenever courier/state/district changes
  useEffect(() => {
    if (editingBranch) return; // Don't auto-generate when editing existing branch
    const selectedCourier = couriers.find((c) => c.courierId === formData.courierId);
    const generated = generateBranchCode(selectedCourier?.name, formData.stateName, formData.districtName);
    if (generated) {
      setFormData((prev) => ({ ...prev, branchCode: generated }));
    }
  }, [formData.courierId, formData.stateName, formData.districtName, couriers, editingBranch]);

  if (!isOpen) return null;

  const handleStateChange = (e) => {
    const selectedStateName = e.target.value;
    const foundState = statesList.find(
      (s) => s.state.toLowerCase() === selectedStateName.toLowerCase()
    );
    setFormData((prev) => ({
      ...prev,
      stateName: foundState ? foundState.state : selectedStateName,
      stateCode: foundState ? foundState.state : selectedStateName,
      districtName: '',
      districtCode: ''
    }));
    setAvailableDistricts(foundState ? foundState.districts : []);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.courierId) errs.courierId = 'Parent Courier Company is required';
    if (!formData.branchName.trim()) errs.branchName = 'Branch name is required';
    if (!formData.branchCode.trim()) errs.branchCode = 'Branch code is required';
    if (!formData.contactPerson.trim()) errs.contactPerson = 'Contact person is required';
    if (!formData.contactNumber.trim()) errs.contactNumber = 'Contact number is required';
    if (!formData.city.trim()) errs.city = 'City is required';
    if (!formData.stateName) errs.stateName = 'State is required';
    if (!formData.districtName) errs.districtName = 'District is required';
    if (!formData.pinCode.trim()) errs.pinCode = 'PIN Code is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        courierId: formData.courierId,
        branchName: formData.branchName.trim(),
        branchCode: formData.branchCode.trim().toUpperCase(),
        contactPerson: formData.contactPerson.trim(),
        contactNumber: formData.contactNumber.trim(),
        alternateContactNumber: formData.alternateContactNumber.trim(),
        email: formData.email.trim(),
        address: {
          addressLine1: formData.addressLine1.trim(),
          addressLine2: formData.addressLine2.trim(),
          city: formData.city.trim(),
          stateName: formData.stateName,
          stateCode: formData.stateCode,
          districtName: formData.districtName,
          districtCode: formData.districtCode,
          pinCode: formData.pinCode.trim()
        },
        active: formData.active
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      console.error('Save branch error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-modal-overlay">
      <div className="cm-modal-container cm-modal-lg">
        <div className="cm-modal-header">
          <div className="cm-modal-title-group">
            <div className="cm-modal-icon-badge">
              <GitBranch size={22} color="#8a6b32" />
            </div>
            <div>
              <h3 className="cm-modal-title">
                {editingBranch ? 'Edit Authorized Branch' : 'Add Authorized Branch'}
              </h3>
              <p className="cm-modal-subtitle">
                A branch created under a courier represents an authorized branch master record.
              </p>
            </div>
          </div>
          <button className="cm-btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="cm-modal-body">
          {/* Section 1: Branch & Parent Courier */}
          <div className="cm-form-section">
            <h4 className="cm-form-section-title">
              <span>1</span> Branch Information
            </h4>
            <div className="cm-form-grid">
              <div className="cm-form-group cm-col-full">
                <label className="cm-label">
                  Parent Courier Company <span className="cm-req">*</span>
                </label>
                <select
                  name="courierId"
                  value={formData.courierId}
                  onChange={handleChange}
                  disabled={Boolean(editingBranch) || Boolean(selectedCourierId)}
                  className={`cm-select ${errors.courierId ? 'cm-input-error' : ''}`}
                >
                  <option value="">-- Select Courier Company --</option>
                  {couriers.map((c) => (
                    <option key={c.courierId} value={c.courierId}>
                      {c.name} ({c.courierId}) {!c.active ? '[Inactive]' : ''}
                    </option>
                  ))}
                </select>
                {errors.courierId && <span className="cm-error-msg">{errors.courierId}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  Branch Name <span className="cm-req">*</span>
                </label>
                <input
                  type="text"
                  name="branchName"
                  value={formData.branchName}
                  onChange={handleChange}
                  placeholder="e.g. Bhubaneswar Main Hub, Cuttack Branch"
                  className={`cm-input ${errors.branchName ? 'cm-input-error' : ''}`}
                />
                {errors.branchName && <span className="cm-error-msg">{errors.branchName}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Branch Code <span className="cm-req">*</span>
                  {!editingBranch && (
                    <span style={{
                      fontSize: '10px',
                      background: '#fef9c3',
                      color: '#854d0e',
                      border: '1px solid #fde68a',
                      borderRadius: '4px',
                      padding: '1px 6px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}>
                      <Zap size={10} /> Auto-generated
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  name="branchCode"
                  value={formData.branchCode}
                  onChange={handleChange}
                  placeholder={!editingBranch ? 'Select courier, state & district to auto-generate' : 'e.g. B-ODISHA-BHUBANESWAR'}
                  className={`cm-input uppercase ${errors.branchCode ? 'cm-input-error' : ''}`}
                  style={!editingBranch && formData.branchCode ? { background: '#f0fdf4', borderColor: '#86efac', fontWeight: '700', letterSpacing: '0.5px' } : {}}
                />
                {errors.branchCode && <span className="cm-error-msg">{errors.branchCode}</span>}
                {!editingBranch && (
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', display: 'block' }}>
                    Format: <strong>[First letter of courier] – [State] – [District]</strong> &nbsp;(you can edit manually)
                  </span>
                )}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  Branch Contact Person <span className="cm-req">*</span>
                </label>
                <div className="cm-input-wrapper">
                  <User size={16} className="cm-input-icon" />
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder="e.g. Soumya Ranjan Das"
                    className={`cm-input ${errors.contactPerson ? 'cm-input-error' : ''}`}
                  />
                </div>
                {errors.contactPerson && <span className="cm-error-msg">{errors.contactPerson}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  Branch Contact Number <span className="cm-req">*</span>
                </label>
                <div className="cm-input-wrapper">
                  <Phone size={16} className="cm-input-icon" />
                  <input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    placeholder="e.g. 98610 12345"
                    className={`cm-input ${errors.contactNumber ? 'cm-input-error' : ''}`}
                  />
                </div>
                {errors.contactNumber && <span className="cm-error-msg">{errors.contactNumber}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Alternate Contact Number</label>
                <div className="cm-input-wrapper">
                  <Phone size={16} className="cm-input-icon" />
                  <input
                    type="tel"
                    name="alternateContactNumber"
                    value={formData.alternateContactNumber}
                    onChange={handleChange}
                    placeholder="e.g. 0674-2534567"
                    className="cm-input"
                  />
                </div>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Branch Email</label>
                <div className="cm-input-wrapper">
                  <Mail size={16} className="cm-input-icon" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="branch.bbsr@courier.com"
                    className="cm-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Address */}
          <div className="cm-form-section">
            <h4 className="cm-form-section-title">
              <span>2</span> Branch Address
            </h4>
            <div className="cm-form-grid">
              <div className="cm-form-group cm-col-full">
                <label className="cm-label">Address Line 1</label>
                <input
                  type="text"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  placeholder="Plot / Shed / Building No., Street, Area"
                  className="cm-input"
                />
              </div>

              <div className="cm-form-group cm-col-full">
                <label className="cm-label">Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Landmark, Industrial Estate"
                  className="cm-input"
                />
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  State <span className="cm-req">*</span>
                </label>
                <select
                  name="stateName"
                  value={formData.stateName}
                  onChange={handleStateChange}
                  className={`cm-select ${errors.stateName ? 'cm-input-error' : ''}`}
                >
                  <option value="">-- Select State --</option>
                  {statesList.map((st) => (
                    <option key={st.state} value={st.state}>
                      {st.state}
                    </option>
                  ))}
                </select>
                {errors.stateName && <span className="cm-error-msg">{errors.stateName}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  District <span className="cm-req">*</span>
                </label>
                <select
                  name="districtName"
                  value={formData.districtName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      districtName: val,
                      districtCode: val
                    }));
                  }}
                  disabled={!formData.stateName}
                  className={`cm-select ${errors.districtName ? 'cm-input-error' : ''}`}
                >
                  <option value="">-- Select District --</option>
                  {availableDistricts.map((dist) => (
                    <option key={dist} value={dist}>
                      {dist}
                    </option>
                  ))}
                </select>
                {errors.districtName && <span className="cm-error-msg">{errors.districtName}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  City <span className="cm-req">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g. Bhubaneswar"
                  className={`cm-input ${errors.city ? 'cm-input-error' : ''}`}
                />
                {errors.city && <span className="cm-error-msg">{errors.city}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  PIN Code <span className="cm-req">*</span>
                </label>
                <input
                  type="text"
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleChange}
                  placeholder="e.g. 751001"
                  maxLength={6}
                  className={`cm-input ${errors.pinCode ? 'cm-input-error' : ''}`}
                />
                {errors.pinCode && <span className="cm-error-msg">{errors.pinCode}</span>}
              </div>

              <div className="cm-form-group cm-col-full">
                <label className="cm-checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                  />
                  <span>Active Branch (Available for order fulfillment & delivery assignments)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="cm-modal-footer">
            <button type="button" className="cm-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="cm-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingBranch ? 'Update Branch' : 'Create Branch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BranchFormModal;
